import { useEffect, useState } from "react";
import { Modal } from "../../modal/Modal";
import { useTranslation } from "react-i18next";
import { ArrowUpDown, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";

interface SearchableMultiSelectReorderModalProps {
  value: string[];
  options: Record<string, string>;
  onReorder: (next: string[]) => void;
  onBeforeOpen?: () => void;
}

export function SearchableMultiSelectReorderModal({
  value,
  options,
  onReorder,
  onBeforeOpen,
}: SearchableMultiSelectReorderModalProps) {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const [items, setItems] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      setItems([...value]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((prev) => {
        const oldIndex = prev.indexOf(String(active.id));
        const newIndex = prev.indexOf(String(over.id));
        if (oldIndex === -1 || newIndex === -1) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleSave = () => {
    onReorder(items);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          onBeforeOpen?.();
          setOpen(true);
        }}
        className="shrink-0 rounded-full p-1.5 text-gray-600 hover:bg-blue-600 hover:text-white dark:text-gray-400 dark:hover:bg-blue-600 dark:hover:text-white"
        aria-label={t("form.reorder")}
      >
        <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        title={t("form.reorder")}
        closeIcon={true}
      >
        <div className="w-full max-w-lg md:min-w-[600px]">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {items.map((optId) => (
                  <SortableOptionRow
                    key={optId}
                    optId={optId}
                    label={options[optId] ?? optId}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <div className="sticky bottom-0 mt-6 flex justify-end gap-2 bg-white pt-2 pb-3 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
            >
              {t("form.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {t("form.validate")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

interface SortableOptionRowProps {
  optId: string;
  label: string;
}

function SortableOptionRow({ optId, label }: SortableOptionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: optId });

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex select-none items-center justify-between gap-4 rounded-lg border bg-white border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
    >
      <span className="min-w-0 truncate text-gray-900 dark:text-gray-100">
        {label}
      </span>
      <div {...attributes} {...listeners} className="cursor-grab shrink-0">
        <GripVertical className="h-5 w-5 text-gray-500 dark:text-gray-400" />
      </div>
    </div>
  );
}
